import React, { ReactNode, useState } from 'react';
import Button from '../../../../atoms/Button/Button';
import ValidationError from '../../../../atoms/Validation/ValidationError';
import {
    initialAddableProductType,
    PaymentMethod,
} from '../../../../../constants/merchantPriceCatalog';
import { ProductRow, validateProductCode } from '../../../../../services/helpers/merchantPrices';
import {
    AddCodeAside,
    AddCodeFields,
    AddCodeForm,
    AddCodeTitle,
    CellInput,
    CellSelect,
} from './styles';

interface AddProductCodeFormProps {
    /** The payment methods a code can be added to in the market and segment of the table, in display order. */
    methods: PaymentMethod[];
    /**
     * Every code priced in the market and segment, whatever its method; a code can only be priced once there. Read when a
     * code is added, so the check sees the current form values.
     */
    existingRows: () => ProductRow[];
    onAddCode: (method: PaymentMethod, productCode: string, productType: string) => void;
    /** Another way to get codes in, shown at the far end of the form, e.g. the standard codes fill. */
    aside?: ReactNode;
}

/** The account types a code of the method can have: the choices when there are several, else just the default. */
const productTypesOf = (method: PaymentMethod): string[] =>
    method.addableProductTypes ?? [method.defaultProductType];

/**
 * Adds an Edge product code to a payment method in the market and segment of the table it sits under: one form for the
 * whole table, with the method chosen in a dropdown and the account type following the method.
 */
const AddProductCodeForm: React.FC<AddProductCodeFormProps> = ({
    methods,
    existingRows,
    onAddCode,
    aside,
}: AddProductCodeFormProps) => {
    const [methodId, setMethodId] = useState(methods[0]?.id);
    const method = methods.find(candidate => candidate.id === methodId) ?? methods[0];
    const [productType, setProductType] = useState(() => initialAddableProductType(method));
    const [productCode, setProductCode] = useState('');
    const [error, setError] = useState<string>();

    const productTypes = productTypesOf(method);

    const changeMethod = (id: string) => {
        const next = methods.find(candidate => candidate.id === id) ?? method;

        setMethodId(next.id);
        setProductType(initialAddableProductType(next));
        setError(undefined);
    };

    const add = () => {
        const problem = validateProductCode(existingRows(), productCode);

        setError(problem);

        if (problem) return;

        onAddCode(method, productCode, productType);
        setProductCode('');
    };

    return (
        <AddCodeForm data-testid="add-product-code-form">
            <AddCodeFields>
                <AddCodeTitle>Add product code</AddCodeTitle>
                <CellSelect
                    value={method.id}
                    aria-label="Payment method for the new product code"
                    data-testid="add-product-method"
                    onChange={event => changeMethod(event.target.value)}
                >
                    {methods.map(candidate => (
                        <option key={candidate.id} value={candidate.id}>
                            {candidate.label}
                        </option>
                    ))}
                </CellSelect>
                <CellSelect
                    value={productType}
                    // With a single account type there is nothing to choose; the dropdown just states it.
                    disabled={productTypes.length === 1}
                    aria-label="Account type for the new product code"
                    data-testid="add-product-type"
                    onChange={event => setProductType(event.target.value)}
                >
                    {productTypes.map(type => (
                        <option key={type} value={type}>
                            {type}
                        </option>
                    ))}
                </CellSelect>
                <CellInput
                    type="text"
                    placeholder="Product code"
                    value={productCode}
                    maxLength={16}
                    aria-label="New product code"
                    data-testid="add-product-code"
                    style={{ textAlign: 'left', width: '16rem' }}
                    $invalid={!!error}
                    onChange={event => {
                        setProductCode(event.target.value);
                        setError(undefined);
                    }}
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            add();
                        }
                    }}
                />
                <Button type="button" data-testid="add-product-code-button" onClick={add}>
                    Add code
                </Button>
                {error && <ValidationError>{error}</ValidationError>}
            </AddCodeFields>
            {aside && <AddCodeAside>{aside}</AddCodeAside>}
        </AddCodeForm>
    );
};

export default AddProductCodeForm;
